import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { api, type QueueItemView } from "../api";
import { statusLabel } from "../domain/statusCopy";
import { prioClass as prioFarbe, queueStateClass, toneTextClass } from "./queueStateStyling";
import { OperatorActionControls } from "./OperatorActionControls";

/**
 * CP-09 — Queue UI: dichte Operator-Sicht auf die Work-Queue.
 *
 * Drei Sichten auf dieselbe begrenzte, serverseitig aggregierte Projektion
 * (/api/control-plane/queue — Item + Feinachse + Scheduling + Mission + Deps):
 *   Tabelle  — filterbar (Queue-State, Priorität, Mission, Scope) + Suche
 *   Kanban   — Spalten NUR aus der echten State-Machine (nichts erfunden)
 *   Warum?   — Explainability je wartendem Item aus der Gate-Wahrheit:
 *              Writer besetzt / Dependency / Scope unverifiziert /
 *              Ressource / Owner-Gate / Verifier — kein Log-Lesen nötig.
 */

const KANBAN_SPALTEN = [
  "READY",
  "RUNNING",
  "WAIT_RESOURCE",
  "WAIT_EXECUTOR",
  "WAIT_EXTERNAL",
  "WAIT_HUMAN",
  "WAIT_OWNER",
  "BLOCKED",
  "PARKED",
  "COMPLETED",
  "FAILED",
] as const;

/** OWNEXP Slice 4 (DC-31): Prioritäten Owner-lesbar; Rohwert im Tooltip. */
const PRIO_LABELS: Record<string, string> = {
  urgent: "Dringend",
  high: "Hoch",
  normal: "Normal",
  low: "Niedrig",
};

function prioLabel(prio: string): string {
  return PRIO_LABELS[prio] ?? prio;
}

/** OWNEXP F-06/F-21: Queue-Ereignisse in Owner-Sprache; Rohcode im title. */
const EVENT_LABEL: Record<string, string> = {
  "queue.auto_intake": "Aufgenommen in die Queue",
  "queue.lease_expired_or_missing": "Alte Lease abgelaufen (neue folgt)",
  "queue.dependency_unmet": "Wartet auf Vorbedingung",
  "queue.dependency_satisfied": "Vorbedingung erfüllt",
  "queue.owner_gate_resolved": "Owner-Entscheidung getroffen",
  "queue.armed_for_auto_execution": "Automatischer Start freigegeben (Arming)",
  "queue.authority_not_managed": "Wartet auf Autoritäts-Überführung (Server-Admission)",
  "queue.NO_ELIGIBLE_WORKER": "Kein ausführender Worker verfügbar",
  "queue.wait_executor": "Wartet auf ausführenden Agenten",
  "queue.worker_available": "Agent verfügbar — automatischer Start",
  "run.created": "Lauf angelegt",
  "run.started": "Lauf gestartet",
  "run.completed": "Lauf abgeschlossen",
  "run.failed": "Lauf fehlgeschlagen",
  "run.cancelled": "Lauf abgebrochen",
  "run.waiting": "Lauf wartet",
};

function eventLabel(raw: string): string {
  return EVENT_LABEL[raw] ?? raw;
}

/** Explainability: der Wartegrund aus denselben Fakten, nach denen der
 *  Supervisor ablehnt — projektionstreu, kein Raten über Logs. */
export function warumLaeuftEsNicht(item: QueueItemView): string | null {
  // OWNEXP SINGLE_TRUTH: Der Server liefert den kanonischen Wartegrund aus
  // derselben Ableitung wie der Supervisor — er hat Vorrang. Die lokale
  // Herleitung ist nur noch Übergangs-Fallback (ältere API/Tests), nie eine
  // zweite Wahrheit neben der Server-Erklärung.
  if (item.warumWartend) return item.warumWartend;
  if (!item.managed) return "Noch nicht von der Queue-State-Machine übernommen (unverwaltet).";
  if (item.queueState === "READY" || item.queueState === "RUNNING" || item.queueState === "READY_TO_RESUME") return null;
  if (item.queueState === "WAIT_OWNER") return `Owner-Entscheidung erforderlich (${item.queueStateReason ?? "owner_gate"}).`;
  if (item.queueState === "WAIT_HUMAN") return `Menschliche Aktion erforderlich (${item.queueStateReason ?? "wait_human"}).`;
  if (item.queueState === "WAIT_EXTERNAL") return `Wartet auf externes Ereignis (${item.queueStateReason ?? "external"}).`;
  if (item.queueState === "WAIT_RESOURCE") return `Ressourcen-Kapazität erschöpft (${item.queueStateReason ?? "resource"}).`;
  if (item.queueState === "BLOCKED") {
    if (item.unmetDependencies.length > 0) {
      return `Blockiert durch unerfüllte Abhängigkeit: ${item.unmetDependencies.map((d) => d.dependsOn).join(", ")}.`;
    }
    return `Blockiert (${item.queueStateReason ?? "dependency_unmet"}).`;
  }
  if (item.queueState === "PARKED") return `Geparkt (${item.queueStateReason ?? "parked"}) — nicht einplanbar, bis aktiviert.`;
  if (item.queueState === "VERIFYING") return "Verifikation läuft.";
  if (item.queueState === "COMPLETED") return null;
  if (item.queueState === "FAILED") return `Fehlgeschlagen (${item.queueStateReason ?? "failed"}) — siehe Retry-/Recovery-Pfad.`;
  if (!item.scopeVerified) return "Scope noch nicht verifiziert (fail-closed, wartet auf Scope-Discovery).";
  return item.queueStateReason ? `Wartet: ${item.queueStateReason}.` : "Wartet.";
}

/** OPNAV1 Slice 6 — Next Action: was als Nächstes passieren muss, damit dieses
 *  Item weiterläuft. Aus demselben Zustand abgeleitet wie der Wartegrund. */
export function naechsteAktion(item: QueueItemView): string {
  // OWNEXP SINGLE_TRUTH: nächste Bedingung vom Server (Supervisor-Ableitung)
  // zitieren, falls vorhanden — dieselbe Aussage wie im Supervisor-Bereich.
  if (item.naechsteBedingung && item.queueState !== "RUNNING" && item.queueState !== "COMPLETED") {
    return item.naechsteBedingung;
  }
  if (!item.managed) return "Wartet auf Übernahme durch die Queue-State-Machine (Scope-Discovery).";
  switch (item.queueState) {
    case "RUNNING":
      return `Läuft unter Lease (${item.leaseHolder ?? "Writer unbekannt"}) bis ${item.leaseExpiresAt ? new Date(item.leaseExpiresAt).toLocaleTimeString("de-DE") : "?"}.`;
    case "READY":
      return "wartet auf Supervisor-Admission im nächsten Tick.";
    case "READY_TO_RESUME":
      return "Executor muss die Writer-Lease neu akquirieren (kanonischer Resume-Pfad).";
    case "WAIT_OWNER":
    case "WAIT_HUMAN":
      return "Owner-Entscheidung im Human-Gate-Inband treffen (Bereich Gates).";
    case "WAIT_EXTERNAL":
      return "externes Ereignis abwarten; Supervisor reconciliert automatisch.";
    case "WAIT_RESOURCE":
      return "auf freie Ressourcen-Kapazität warten; Recheck im nächsten Tick.";
    case "BLOCKED":
      return item.unmetDependencies.length > 0
        ? `Dependency(n) ${item.unmetDependencies.map((d) => d.dependsOn).join(", ")} abschließen.`
        : "Blocker auflösen (siehe Grund).";
    case "PARKED":
      return "aktivieren (Owner), dann Admission durch Supervisor.";
    case "FAILED":
      return "Retry-/Recovery-Pfad prüfen (Attempts/Evidence in der Missionsdetail-Ansicht).";
    case "VERIFYING":
      return "Verifikation läuft — Ergebnis erscheint als Transition.";
    case "COMPLETED":
      return "abgeschlossen — Report/Evidence im Bereich Evidence & Reports.";
    default:
      return item.scopeVerified ? "wartet auf Zustandswechsel durch Supervisor." : "Scope-Verifikation ausstehend (fail-closed).";
  }
}

type Sicht = "tabelle" | "kanban";

/**
 * S0.9a QUEUE READ-PATH TRUTH — Wahrheitsgrammatik für den Queue-Refresh.
 *
 * LOADING   explizit (Fläche sagt „wird geladen", nie stiller Wert)
 * LIVE      letzter Refresh erfolgreich
 * STALE     Refresh fehlgeschlagen, aber Vorhanden-Daten bleiben sichtbar
 *           (mit Zeitpunkt der letzten erfolgreichen Aktualisierung)
 * ERROR     Refresh fehlgeschlagen und keine Vorhanden-Daten (Kurztext)
 * „—"       nur als expliziter Lade-/Unbekannt-Zustand, nie stiller
 *           Wahrheitswert (NO_FALSE_ZERO, analog resourceState.ts aus S0.2–S0.4).
 */
export type QueueTruthStatus = "LOADING" | "LIVE" | "STALE" | "ERROR";

export function istTimeoutFehler(msg: string): boolean {
  return msg.includes("code=TIMEOUT") || msg.toLowerCase().includes("timeout");
}

/** Kurztext für Banner/Chip (eine Zeile, gekürzt, ohne Stack-Späne). */
export function fehlerKurztext(msg: string): string {
  return istTimeoutFehler(msg) ? "Zeitüberschreitung" : msg.split("\n")[0].slice(0, 120);
}

function uhrzeit(epoch: number): string {
  return new Date(epoch).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

/** OWNEXP Slice 5 (R-Q-6): gespeicherte Ansicht = Filter/Sicht/Suche in der
 *  URL-Hash-Query (`#/control/queue?state=…&prio=…&mission=…&q=…&sicht=…`).
 *  Back/Forward stellt die Ansicht wieder her (R-NAV-4) — Hash ist Navigation. */
function queueAnsichtAusHash(hash: string): { state: string; prio: string; mission: string; q: string; sicht: Sicht } {
  const query = hash.includes("?") ? hash.slice(hash.indexOf("?") + 1) : "";
  const params = new URLSearchParams(query);
  const sicht = params.get("sicht");
  return {
    // Normalisiert (Server-Whitelist ist Uppercase) — Client-Filter und Fetch
    // sehen denselben Wert, kein unsichtbares „0 Items" bei kleingeschriebenen
    // Deep-Links (Final-Verifier-Abweichung 3).
    state: (params.get("state") ?? "").toUpperCase(),
    prio: params.get("prio") ?? "",
    mission: params.get("mission") ?? "",
    q: params.get("q") ?? "",
    sicht: sicht === "kanban" ? "kanban" : "tabelle",
  };
}

function hashMitAnsicht(ansicht: { state: string; prio: string; mission: string; q: string; sicht: Sicht }): string {
  const params = new URLSearchParams();
  if (ansicht.state) params.set("state", ansicht.state);
  if (ansicht.prio) params.set("prio", ansicht.prio);
  if (ansicht.mission) params.set("mission", ansicht.mission);
  if (ansicht.q) params.set("q", ansicht.q);
  if (ansicht.sicht === "kanban") params.set("sicht", "kanban");
  const query = params.toString();
  return `#/control/queue${query ? `?${query}` : ""}`;
}

export function QueuePanel({ onOpenMission, refreshKey = 0 }: { onOpenMission?: (missionId: string) => void; refreshKey?: number } = {}): JSX.Element {
  const startAnsicht = useMemo(() => queueAnsichtAusHash(window.location.hash), []);
  const [items, setItems] = useState<QueueItemView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // S0.9a: Zeitpunkt des letzten autoritativen Erfolgs — Basis für die
  // STALE-Markierung („Veraltete Daten — letzte erfolgreiche Aktualisierung …").
  const [lastSuccessfulFetch, setLastSuccessfulFetch] = useState<number | null>(null);
  //_S0.9a: max. 1 sofortiger manueller Retry pro Ladezyklus — danach gilt der
  //_normale Poll-Takt (refreshKey), kein Retry-Sturm bei Timeout/Proxy-Stall.
  const [retryVerbraucht, setRetryVerbraucht] = useState(false);
  const [sicht, setSicht] = useState<Sicht>(startAnsicht.sicht);
  const [stateFilter, setStateFilter] = useState<string>(startAnsicht.state);
  const [prioFilter, setPrioFilter] = useState<string>(startAnsicht.prio);
  const [missionFilter, setMissionFilter] = useState<string>(startAnsicht.mission);
  const [suche, setSuche] = useState(startAnsicht.q);
  const [drilldown, setDrilldown] = useState<string | null>(null);
  // OWNEXP Live-Deckel: Cursor-Pagination — „Weitere laden“ hängt den NÄCHSTEN
  // 8er-Chuck an (offset), statt einen größeren Request zu machen (Proxy-Kappe).
  const [mehrUrl, setMehrUrl] = useState(false);
  const [laedtMehr, setLaedtMehr] = useState(false);
  //_EMBER_CALM §9: Filter-Disclosure + Schnellfilter (Chips) — die volle
  //_Suche/Filter-Leiste ist eingeklappt; Deep-Links mit Filtern öffnen sie.
  const [filterOffen, setFilterOffen] = useState(
    Boolean(startAnsicht.state || startAnsicht.prio || startAnsicht.mission || startAnsicht.q),
  );
  const [schnellFilter, setSchnellFilter] = useState<string>("");

  // EMBER_CALM UX H — Zweckmotion: Ein Item, dessen Zustand sich zwischen zwei
  // Läufen ändert, blitzt einmal kurz in der Statuszelle auf („hier hat sich
  // etwas verändert"). Rein erklärend, kein Loop; bei prefers-reduced-motion
  // schaltet die CSS-Regel die Animation stumm.
  const vorherigeZustaende = useRef<Map<string, string>>(new Map());
  const [statusBlitz, setStatusBlitz] = useState<Set<string>>(new Set());
  useEffect(() => {
    const geaendert = new Set<string>();
    for (const it of items) {
      const vorher = vorherigeZustaende.current.get(it.id);
      if (vorher !== undefined && vorher !== (it.queueState ?? "")) geaendert.add(it.id);
      vorherigeZustaende.current.set(it.id, it.queueState ?? "");
    }
    if (geaendert.size === 0) return;
    setStatusBlitz(geaendert);
    const timer = setTimeout(() => setStatusBlitz(new Set()), 800);
    return () => clearTimeout(timer);
  }, [items]);

  // Ansicht in die URL schreiben (R-Q-6): Filterwechsel erzeugen einen
  // Hash-Eintrag; der Zustand wird sofort gesetzt (schnelles Feedback) UND
  // der hashchange-Listener gleicht Back/Forward/Deep-Links ab.
  const ansichtInUrl = (next: { state?: string; prio?: string; mission?: string; q?: string; sicht?: Sicht }): void => {
    if (next.state !== undefined) setStateFilter(next.state);
    if (next.prio !== undefined) setPrioFilter(next.prio);
    if (next.mission !== undefined) setMissionFilter(next.mission);
    if (next.q !== undefined) setSuche(next.q);
    if (next.sicht !== undefined) setSicht(next.sicht);
    window.location.hash = hashMitAnsicht({
      state: next.state ?? stateFilter,
      prio: next.prio ?? prioFilter,
      mission: next.mission ?? missionFilter,
      q: next.q ?? suche,
      sicht: next.sicht ?? sicht,
    });
  };

  // Hash-Änderungen (Back/Forward, Deep-Link) übernehmen: URL ist Wahrheit.
  // UX-J Red-Team F4: Nur bei TATSÄCHLICH geändertem Hash-Zustand übernehmen
  // und nachladen — eigene Filterwechsel (ansichtInUrl) setzen State UND Hash
  // synchron, der hashchange wäre sonst ein zweiter, redundanter Load neben
  // dem [stateFilter]-Effect (Deckel-Last ohne Grund).
  useEffect(() => {
    const onHash = (): void => {
      if (!window.location.hash.startsWith("#/control/queue")) return;
      const a = queueAnsichtAusHash(window.location.hash);
      const unveraendert =
        a.sicht === sicht && a.state === stateFilter && a.prio === prioFilter && a.mission === missionFilter && a.q === suche;
      setSicht(a.sicht);
      setStateFilter(a.state);
      setPrioFilter(a.prio);
      setMissionFilter(a.mission);
      setSuche(a.q);
      // Deep-Link-Wechsel von außen (Palette/Back) laden sofort nach (H1-Rest);
      // bei eigenen Pushes hat der Filter-Effect den Load bereits ausgelöst.
      if (!unveraendert) load();
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sicht, stateFilter, prioFilter, missionFilter, suche]);

  const load = (viaRetry = false): void => {
    setLoading(true);
    setError(null);
    // Nur reguläre Loads (Poll/Filter/Deep-Link) geben den manuellen Retry
    // wieder frei — der Retry selbst nicht (kein Retry-Sturm, s.o.).
    if (!viaRetry) setRetryVerbraucht(false);
    // OWNEXP Red-Team H1: Zwei-Stufen-Ladung statt hartem 8-Item-Limit —
    // (1) die 50 wichtigsten gemischten Items (Wait-States zuerst, Proxy-
    // transportdeckel bleibt gewahrt), (2) ALLE fehlgeschlagenen Items
    // (bounded ?state=FAILED), damit Fehler nie unsichtbar bleiben und
    // Palette-Deep-Links nie in ein Falsch-Negativ laufen. Aktiv gesetzter
    // State-Filter wird serverseitig gefiltert geladen.
    // State normalisieren (Server-Whitelist ist Uppercase) — kein stiller
    // Fail-open bei handgeschriebenen Deep-Links (Red-Team N4).
    const stateRaw = new URLSearchParams(window.location.hash.includes("?") ? window.location.hash.slice(window.location.hash.indexOf("?") + 1) : "").get("state") ?? stateFilter;
    const stateAusHash = stateRaw ? stateRaw.toUpperCase() : "";
    // Live-Fund (Proxy-Transportdeckel ~8–12 KB): un gefiltert bleibt die
    // Erstladung beim bewährten 8er-Chuck (Voll-Load stallt den Proxy);
    // Vollständigkeit über serverseitige q/state-Filter (kleine Mengen) und
    // den „Weitere laden“-Chuk unten — kein Falsch-Negativ, keine Lüge.
    const qSuche = suche.trim().length >= 2 ? suche.trim() : null;
    const haupt: Promise<{ items: QueueItemView[] }> = stateAusHash
      ? api.controlPlaneQueue(200, stateAusHash)
      : qSuche
        ? api.controlPlaneQueue(50, undefined, qSuche)
        : Promise.all([api.controlPlaneQueue(8), api.controlPlaneQueue(200, "FAILED")]).then(([a, b]) => {
            const ids = new Set(a.items.map((it) => it.id));
            setMehrUrl((a.items.length >= 8) || ((a.truncated ?? 0) > 0));
            return { ...a, items: [...a.items, ...b.items.filter((it) => !ids.has(it.id))] };
          });
    haupt
      .then((r) => {
        setItems(r.items);
        // S0.9a: Erfolg — STALE/ERROR verschwinden, Erfolgsmarke aktualisiert.
        setError(null);
        setLastSuccessfulFetch(Date.now());
      })
      .catch((e) => {
        // S0.9a: Fehlschlag — Vorhanden-Daten BLEIBEN sichtbar (kein Weg-
        // werfen), werden aber über `error` + lastSuccessfulFetch explizit
        // STALE-markiert; ohne Vorhanden-Daten ist es ein ehrlicher ERROR.
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);
  // OWNEXP Slice 4 (IA-C3): kein eigener Aktualisieren-Button mehr — die
  // Fläche triggert den Nachlauf über den Refresh-Key.
  useEffect(() => {
    if (refreshKey > 0) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);
  // OWNEXP Red-Team H1-Rest: Filterwechsel/Deep-Link MUSS sofort neu laden —
  // sonst zeigt ein state=FAILED-Deep-Link nach „Alle States" bis zum nächsten
  // Takt ein falsches Gesamtbild (umgekehrte Wahrheitslüge).
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateFilter]);

  const missionen = useMemo(() => {
    const set = new Map<string, string>();
    for (const it of items) if (it.missionId) set.set(it.missionId, it.missionGoal ?? it.missionId);
    return [...set.entries()];
  }, [items]);

  const qSuche = suche.trim().length >= 2 ? suche.trim() : null;
  //_Schnellfilter-Mengen (EMBER_CALM §9): ein Chip filtert über dieselbe
  //_Queue-Wahrheit wie die Detail-Selects — keine zweite State-Wahrheit.
  const SCHNELL_MENGEN: Record<string, readonly string[]> = {
    "braucht-dich": ["WAIT_OWNER", "WAIT_HUMAN"],
    laeuft: ["RUNNING"],
    wartet: ["WAIT_RESOURCE", "WAIT_EXTERNAL", "WAIT_EXECUTOR", "READY_TO_RESUME"],
    fehler: ["FAILED", "BLOCKED"],
  };
  const gefiltert = useMemo(() => {
    const q = suche.trim().toLowerCase();
    const schnellMenge = SCHNELL_MENGEN[schnellFilter];
    return items.filter((it) => {
      if (schnellMenge && !schnellMenge.includes(it.queueState ?? "")) return false;
      if (stateFilter && (it.queueState ?? "(unverwaltet)") !== stateFilter) return false;
      if (prioFilter && it.priority !== prioFilter) return false;
      if (missionFilter && it.missionId !== missionFilter) return false;
      if (q) {
        const heu = `${it.id} ${it.title} ${it.goal ?? ""} ${it.writerScope ?? ""} ${it.missionId ?? ""}`.toLowerCase();
        if (!heu.includes(q)) return false;
      }
      return true;
    });
  }, [items, stateFilter, prioFilter, missionFilter, suche, schnellFilter]);

  // S0.9a QUEUE READ-PATH TRUTH — abgeleiteter Wahrheitszustand der Fläche.
  const truth: QueueTruthStatus = loading
    ? "LOADING"
    : error
      ? items.length > 0
        ? "STALE"
        : "ERROR"
      : "LIVE";

  return (
    <div className="space-y-3" aria-label="Queue" data-testid="queue-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">Queue</h2>
        <div className="flex items-center gap-2">
          <div className="flex rounded border border-jarvis-border overflow-hidden text-xs">
            {/* UX-J Red-Team F5: aria-pressed + 44px-Targets (wie Schnellfilter) */}
            <button
              className={`min-h-11 px-2.5 py-1 ${sicht === "tabelle" ? "bg-jarvis-accent/20 text-jarvis-text" : "text-jarvis-muted"}`}
              aria-pressed={sicht === "tabelle"}
              onClick={() => ansichtInUrl({ sicht: "tabelle" })}
            >
              Tabelle
            </button>
            <button
              className={`min-h-11 px-2.5 py-1 ${sicht === "kanban" ? "bg-jarvis-accent/20 text-jarvis-text" : "text-jarvis-muted"}`}
              aria-pressed={sicht === "kanban"}
              onClick={() => ansichtInUrl({ sicht: "kanban" })}
            >
              Kanban
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div
          className={`card p-4 text-sm ${items.length > 0 ? "border-yellow-500/40 text-jarvis-text" : toneTextClass("danger")}`}
          role="alert"
          data-testid={items.length > 0 ? "queue-stale-banner" : "queue-error-banner"}
        >
          {items.length > 0 ? (
            // S0.9a (1a): STALE — Vorhanden-Daten bleiben sichtbar, aber
            // explizit als veraltet markiert, mit Zeitpunkt des letzten
            // Erfolgs und Kurztext des Fehlschlags. Kein stiller Wert.
            <>
              {istTimeoutFehler(error) ? (
                // S0.9a (1c): Timeout + Vorhanden-Daten als Klartext-Zustand.
                <>Queue nicht abrufbar — Zeitüberschreitung; zeige veralteten Stand (letzte erfolgreiche Aktualisierung:{" "}
                {lastSuccessfulFetch != null ? uhrzeit(lastSuccessfulFetch) : "unbekannt"})</>
              ) : (
                <>
                  Veraltete Daten — letzte erfolgreiche Aktualisierung:{" "}
                  {lastSuccessfulFetch != null ? uhrzeit(lastSuccessfulFetch) : "unbekannt"}; Aktualisierung fehlgeschlagen (
                  {fehlerKurztext(error)})
                </>
              )}
            </>
          ) : istTimeoutFehler(error) ? (
            // S0.9a (1c): Timeout als Klartext-Zustand — 15 s, code=TIMEOUT.
            <>Queue nicht abrufbar — Zeitüberschreitung</>
          ) : (
            <>Queue nicht abrufbar: {fehlerKurztext(error)}</>
          )}
          {/* S0.9a (1c): max. 1 sofortiger manueller Retry pro Ladezyklus,
              danach normaler Poll-Takt — kein Retry-Sturm. */}
          <button
            type="button"
            className="btn min-h-8 px-2.5 py-1 text-[11px] ml-2"
            disabled={loading || retryVerbraucht}
            onClick={() => {
              if (retryVerbraucht) return;
              setRetryVerbraucht(true);
              load(true);
            }}
            data-testid="queue-retry"
          >
            Erneut versuchen
          </button>
        </div>
      )}
      {loading && items.length > 0 && (
        <p className="px-1 text-[11px] text-jarvis-muted" role="status" aria-live="polite" data-testid="queue-reloading">
          Aktualisiere Queue-Daten …
        </p>
      )}

      {/* EMBER_CALM §9 — Suche & Filter PROGRESSIVE DISCLOSURE: Default
          eingeklappt (ein Kompakt-Trigger + Quick-Filter-Chips); die volle
          Suche/Filter-Leiste öffnet erst auf Klick. Deep-Links mit Filtern
          öffnen automatisch. */}
      <div className="card p-3 mb-2" data-testid="queue-filters">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <button
            type="button"
            className="btn min-h-8 px-2.5 py-1"
            aria-expanded={filterOffen}
            aria-controls="queue-filters-voll"
            onClick={() => setFilterOffen((o) => !o)}
            data-testid="queue-filter-toggle"
          >
            {filterOffen ? "Suche & Filter ▴" : "Suche & Filter ▾"}
          </button>
          <div className="flex flex-wrap items-center gap-1" role="group" aria-label="Schnellfilter">
            {(
              [
                ["", "Alle"],
                ["braucht-dich", "Braucht dich"],
                ["laeuft", "Läuft"],
                ["wartet", "Wartet"],
                ["fehler", "Fehler"],
              ] as const
            ).map(([wert, label]) => (
              <button
                key={wert || "alle"}
                type="button"
                aria-pressed={schnellFilter === wert}
                className={`min-h-11 rounded-full border px-2.5 py-1 text-[11px] transition-colors ${
                  schnellFilter === wert
                    ? "border-jarvis-accent/60 bg-jarvis-accent/20 text-jarvis-text"
                    : "border-jarvis-border text-jarvis-muted hover:text-jarvis-text"
                }`}
                onClick={() => setSchnellFilter(wert)}
                data-testid={`queue-quick-${wert || "alle"}`}
              >
                {label}
              </button>
            ))}
          </div>
          <span className="ml-auto text-jarvis-muted" data-testid="queue-count">
            {/* S0.9a (1b): Zähler-Grammatik — LOADING/LIVE/STALE(mit Zeit)/
                ERROR(Kurztext); „—" nur als expliziter Lade-/Unbekannt-
                Zustand, nie stiller Wahrheitswert (NO_FALSE_ZERO). */}
            {(loading && items.length === 0) || (!loading && !error && items.length === 0 && lastSuccessfulFetch == null)
              ? "— wird geladen"
              : `${gefiltert.length} / ${items.length} Items`}{" "}
            ·{" "}
            <span data-testid="queue-truth">
              {truth === "LOADING"
                ? "LOADING"
                : truth === "STALE"
                  ? `STALE (${lastSuccessfulFetch != null ? uhrzeit(lastSuccessfulFetch) : "unbekannt"})`
                  : truth === "ERROR"
                    ? `ERROR (${fehlerKurztext(error ?? "unbekannt")})`
                    : "LIVE"}
            </span>
          </span>
        </div>
        {filterOffen && (
          <div id="queue-filters-voll" className="mt-2 flex flex-wrap gap-2 items-center text-xs">
            <input
              className="input min-h-8 flex-1 min-w-[12rem] text-sm"
              placeholder="Suche (Id, Titel, Ziel, Scope, Mission)…"
              value={suche}
              onChange={(e) => {
                // Suche per replaceState in der URL (teilbar, kein History-Spam
                // pro Tastenanschlag); Filter-Selects erzeugen History-Einträge.
                setSuche(e.target.value);
                window.history.replaceState(null, "", hashMitAnsicht({ state: stateFilter, prio: prioFilter, mission: missionFilter, q: e.target.value, sicht }));
              }}
              aria-label="Queue-Suche"
            />
            <select className="input min-h-8" value={stateFilter} onChange={(e) => ansichtInUrl({ state: e.target.value })} aria-label="Filter Queue-State">
              <option value="">Alle States</option>
              {[...new Set(items.map((it) => it.queueState ?? "(unverwaltet)"))].sort().map((s) => (
                <option key={s} value={s}>
                  {statusLabel(s) ?? s}
                </option>
              ))}
            </select>
            <select className="input min-h-8" value={prioFilter} onChange={(e) => ansichtInUrl({ prio: e.target.value })} aria-label="Filter Priorität">
              <option value="">Alle Prioritäten</option>
              <option value="urgent">Dringend</option>
              <option value="high">Hoch</option>
              <option value="normal">Normal</option>
              <option value="low">Niedrig</option>
            </select>
            <select className="input min-h-8 max-w-[16rem]" value={missionFilter} onChange={(e) => ansichtInUrl({ mission: e.target.value })} aria-label="Filter Mission">
              <option value="">Alle Missionen</option>
              {missionen.map(([id, goal]) => (
                <option key={id} value={id}>
                  {(goal.length > 40 ? `${goal.slice(0, 40)}…` : goal) + ` (${id})`}
                </option>
              ))}
            </select>
          </div>
        )}
        {!stateFilter && !qSuche && mehrUrl && (
          <button
            type="button"
            className="btn mt-2 min-h-8 px-2 py-1 text-[11px]"
            disabled={laedtMehr}
            onClick={() => {
              setLaedtMehr(true);
              const offsetNext = items.filter((it) => it.queueState !== "FAILED").length;
              api
                .controlPlaneQueue(8, undefined, undefined, offsetNext)
                .then((r) => {
                  const ids = new Set(items.map((it) => it.id));
                  setItems([...items, ...r.items.filter((it) => !ids.has(it.id))]);
                  setMehrUrl(r.items.length >= 8 || (r.truncated ?? 0) > 0);
                })
                .catch(() => setMehrUrl(false))
                .finally(() => setLaedtMehr(false));
            }}
            data-testid="queue-load-more"
          >
            {laedtMehr ? "lädt…" : "Weitere 8 laden"}
          </button>
        )}
      </div>

      {sicht === "tabelle" ? (
        <>
        {/* OWNEXP Slice 12 (RC/DC-54): Desktop-Tabelle + mobile Kartenansicht —
            strukturelle Anpassung statt horizontalem Scroll-Schieben. Die
            Umschaltung läuft über die Design-System-Media-Queries
            (simo-ds-desktop-table / simo-ds-mobile-table). */}
        {/* EMBER_CALM Slice A — QUEUE_INTERNAL_SCROLL: Die Item-Liste besitzt
            den Hauptscroll (eigener Container, overscroll-contain), die App-Shell
            (Topbar/Navigation/Queue-Header) bleibt beim Scrollen vieler Items
            stabil — kein Wegscrollen der ganzen App mehr. */}
        <div className="card overflow-auto overscroll-contain simo-thin-scroll p-0 simo-ds-desktop-table max-h-[min(70vh,calc(100dvh-260px))]" tabIndex={0} role="region" aria-label="Queue-Tabelle" data-testid="queue-table">
          <table className="w-full text-xs sm:text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-jarvis-muted border-b border-jarvis-border">
                <th className="p-2">Titel</th>
                <th className="p-2">State</th>
                <th className="p-2">Warum wartet es?</th>
                <th className="p-2 hidden lg:table-cell">Nächste Aktion</th>
                <th className="p-2">Prio</th>
                <th className="p-2 hidden md:table-cell">Scope</th>
                <th className="p-2 hidden xl:table-cell">Mission</th>
                <th className="p-2 hidden lg:table-cell">Writer / Lease</th>
                <th className="p-2 hidden 2xl:table-cell">Ressource</th>
                <th className="p-2 hidden 2xl:table-cell">Letztes Event</th>
                <th className="p-2 hidden 2xl:table-cell">Aktualisiert</th>
              </tr>
            </thead>
            <tbody>
              {gefiltert.length === 0 && (
                <tr>
                  <td colSpan={11} className="p-4 text-jarvis-muted text-sm">
                    Keine Items für diese Filter.
                  </td>
                </tr>
              )}
              {gefiltert.map((it) => {
                const warum = warumLaeuftEsNicht(it);
                const offen = drilldown === it.id;
                return (
                  <Fragment key={it.id}>
                  <tr
                    className={`border-b border-jarvis-border/60 last:border-0 align-top hover:bg-jarvis-bg/40 cursor-pointer scroll-mt-40 ${offen ? "bg-jarvis-bg/40" : ""}`}
                    data-testid={`queue-row-${it.id}`}
                    onClick={() => setDrilldown(offen ? null : it.id)}
                    aria-label={`${it.title} — ${offen ? "Details eingeklappt" : "Details aufklappen"}`}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setDrilldown(offen ? null : it.id);
                      }
                    }}
                  >
                    <td className="p-2 max-w-[18rem]">
                      <div className="font-medium truncate" title={it.title}>
                        <span aria-hidden className="mr-1 inline-block transition-transform" style={{ transform: offen ? "rotate(90deg)" : "none" }}>▸</span>
                        {it.title}
                      </div>
                      <div className="font-mono text-[10px] text-jarvis-muted truncate">{it.id}</div>
                    </td>
                    <td className={`p-2 ${statusBlitz.has(it.id) ? "simo-state-flash" : ""}`}>
                      {/* OWNEXP Slice 4 (DC-31): Owner-Label primär, Rohcode sekundar */}
                      <span title={it.queueState ?? undefined}>{statusLabel(it.queueState) ?? "Unbekannter Zustand"}</span>
                      {it.queueState && (
                        <div className="font-mono text-[10px] text-jarvis-muted">{it.queueState}</div>
                      )}
                      {it.leaseHolder && (
                        <div className="text-[10px] text-jarvis-muted">Lease: {it.leaseHolder}</div>
                      )}
                    </td>
                    <td className="p-2 max-w-[16rem]">
                      {warum ? (
                        <span className={toneTextClass("waiting")}>{warum}</span>
                      ) : (
                        <span className={toneTextClass("success")}>{it.queueState === "COMPLETED" ? "abgeschlossen" : "lauf-/planbar"}</span>
                      )}
                      {it.unmetDependencies.length > 0 && (
                        <div className="text-[10px] text-jarvis-muted font-mono">
                          wartet auf: {it.unmetDependencies.map((d) => d.dependsOn).join(", ")}
                        </div>
                      )}
                      {it.blocksCount > 0 && (
                        <div className="text-[10px] text-jarvis-muted">blockiert {it.blocksCount} weitere</div>
                      )}
                    </td>
                    <td className="p-2 max-w-[16rem] text-[11px] text-jarvis-muted hidden lg:table-cell">{naechsteAktion(it)}</td>
                    <td className={`p-2 font-medium ${prioFarbe(it.priority)}`} title={it.priority}>{prioLabel(it.priority)}</td>
                    <td className="p-2 hidden md:table-cell font-mono text-[11px]">
                      {it.writerScope ?? "—"}
                      {it.writerScope && (
                        <span className={it.scopeVerified ? toneTextClass("success") : toneTextClass("warning")}>
                          {it.scopeVerified ? " ✓" : " ⚠ unverifiziert"}
                        </span>
                      )}
                    </td>
                    <td className="p-2 hidden xl:table-cell text-[11px] max-w-[10rem]">
                      {/* UX-J Red-Team F3: Missions-Ziel primär (wie im Drilldown),
                          Kennung mono sekundär — Rohcode nie Zeilen-Haupttext. */}
                      <div className="truncate" title={it.missionGoal ?? it.missionId ?? ""}>{it.missionGoal ?? it.missionId ?? "—"}</div>
                      <div className="font-mono text-[10px] text-jarvis-muted truncate">{it.missionId ?? ""}</div>
                    </td>
                    <td className="p-2 hidden lg:table-cell font-mono text-[11px]">
                      {it.leaseHolder ?? "frei"}
                      {it.leaseExpiresAt && (
                        <div className="text-[10px] text-jarvis-muted">bis {new Date(it.leaseExpiresAt).toLocaleTimeString("de-DE")}</div>
                      )}
                    </td>
                    <td className="p-2 hidden 2xl:table-cell font-mono text-[11px] max-w-[10rem] truncate" title={it.resourceClass ?? ""}>
                      {it.resourceClass ? it.resourceClass.replace(/[{}"]/g, "").slice(0, 40) || "—" : "—"}
                    </td>
                    <td className="p-2 hidden 2xl:table-cell text-[11px] text-jarvis-muted whitespace-nowrap">
                      {it.lastEvent ? (
                        <>
                          <span title={it.lastEvent.eventType}>{eventLabel(it.lastEvent.eventType)}</span>
                          <div className="text-[10px] text-jarvis-muted">{new Date(it.lastEvent.occurredAt).toLocaleString("de-DE")}</div>
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="p-2 hidden 2xl:table-cell text-[11px] text-jarvis-muted whitespace-nowrap">
                      {new Date(it.updatedAt).toLocaleString("de-DE")}
                    </td>
                  </tr>
                  {offen && (
                    <tr key={`${it.id}-drilldown`} data-testid={`queue-drilldown-${it.id}`} className="border-b border-jarvis-border/60">
                      <td colSpan={11} className="bg-black/30 p-4">
                        <div className="grid gap-3 text-xs md:grid-cols-2 xl:grid-cols-3">
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-jarvis-muted">Ziel</div>
                            <p className="mt-1 leading-relaxed">{it.goal ?? "—"} <button type="button" className="btn min-h-8 px-2 py-1 text-[10px]" onClick={() => navigator.clipboard?.writeText(it.id)}>Kennung kopieren</button></p>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-jarvis-muted">Abhängigkeiten</div>
                            <p className="mt-1">{it.unmetDependencies.length === 0 ? "Keine offenen Abhängigkeiten." : it.unmetDependencies.map((d) => `${d.dependsOn} (${d.kind})`).join(", ")}</p>
                            {it.blocksCount > 0 && <p className="mt-1 text-jarvis-muted">Blockiert selbst {it.blocksCount} weitere Items.</p>}
                          </div>
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-jarvis-muted">Writer / Lease / Ressource</div>
                            <p className="mt-1">
                              {it.leaseHolder ?? "kein Writer aktiv (frei)"}
                              {it.leaseExpiresAt ? ` — Lease bis ${new Date(it.leaseExpiresAt).toLocaleString("de-DE")}` : ""}
                              {it.resourceClass ? ` · Ressource: ${it.resourceClass.replace(/[{}"]/g, "")}` : ""}
                            </p>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-jarvis-muted">Mission</div>
                            <p className="mt-1">
                              {it.missionId ? (
                                <>
                                  <button type="button" className="underline decoration-jarvis-accent/60 underline-offset-2" onClick={() => onOpenMission?.(it.missionId ?? "")}>
                                    {it.missionGoal ?? it.missionId}
                                  </button>{" "}
                                  <span className="font-mono text-[10px] text-jarvis-muted">{it.missionId}</span>
                                </>
                              ) : "Keine Mission-Bindung."}
                            </p>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-jarvis-muted">Technische Details</div>
                            <p className="mt-1 font-mono text-[10px] leading-relaxed text-jarvis-muted">
                              queueState={it.queueState ?? "null"} · reason={it.queueStateReason ?? "null"} · scope={it.writerScope ?? "null"} (verified={String(it.scopeVerified)}) · id={it.id}
                            </p>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-jarvis-muted">Letztes Ereignis</div>
                            <p className="mt-1">
                              {it.lastEvent
                                ? `${eventLabel(it.lastEvent.eventType)} (${it.lastEvent.eventType}) — ${new Date(it.lastEvent.occurredAt).toLocaleString("de-DE")}`
                                : "Kein Ereignis aufgezeichnet."}
                            </p>
                          </div>
                          {/* OWNEXP Slice 8: echte, serverseitig autorisierte
                              Operator-Aktionen im Drilldown (keine Fake-Buttons) */}
                          <div className="md:col-span-2 xl:col-span-3">
                            <OperatorActionControls item={it} onAenderung={load} />
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Mobile Kartenansicht (<768px, Design-System-Media-Query): dieselben
            Daten, dieselben Drilldowns/Aktionen — kein Querscrollen.
            UX-J Red-Team F2: eigener Scrollbereich wie Tabelle/Kanban (Shell
            bleibt stabil — der Live-Vertrag gilt auch auf Phone), Titel geclampt. */}
        <div className="simo-ds-mobile-table overflow-auto overscroll-contain simo-thin-scroll max-h-[min(70vh,calc(100dvh-320px))]" data-testid="queue-mobile-cards" role="region" aria-label="Queue mobile Ansicht">
          {gefiltert.length === 0 && <p className="card p-4 text-sm text-jarvis-muted">Keine Items für diese Filter.</p>}
          {gefiltert.map((it) => {
            const offen = drilldown === it.id;
            return (
              <article key={it.id} className="card p-3" data-testid={`queue-mobile-${it.id}`}>
                <button
                  type="button"
                  className="w-full text-left"
                  aria-expanded={offen}
                  onClick={() => setDrilldown(offen ? null : it.id)}
                  data-testid={`queue-mobile-toggle-${it.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <strong className="min-w-0 text-sm leading-snug line-clamp-2" title={it.title}>{it.title}</strong>
                    <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-medium ${statusBlitz.has(it.id) ? "simo-state-flash " : ""}${queueStateClass(it.queueState ?? "")}`} title={it.queueState ?? undefined}>
                      {statusLabel(it.queueState) ?? "Zustand unbekannt"}
                    </span>
                  </div>
                  <div className="mt-1 text-[11px] text-jarvis-muted">
                    {prioLabel(it.priority)} · {it.leaseHolder ? `Writer: ${it.leaseHolder}` : "kein Writer aktiv"}
                  </div>
                  {warumLaeuftEsNicht(it) && <p className={`mt-1 text-[11px] ${toneTextClass("waiting")}`}>{warumLaeuftEsNicht(it)}</p>}
                </button>
                {offen && (
                  <div className="mt-2 border-t border-jarvis-border/60 pt-2 text-[11px] text-jarvis-muted">
                    <p>Nächste Aktion: {naechsteAktion(it)}</p>
                    {it.missionId && <p className="mt-1 font-mono text-[10px]">{it.missionId}</p>}
                    <div className="mt-2"><OperatorActionControls item={it} onAenderung={load} /></div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
        </>
      ) : (
        <div className="overflow-auto overscroll-contain simo-thin-scroll max-h-[min(70vh,calc(100dvh-260px))]" tabIndex={0} role="region" aria-label="Queue-Kanban" data-testid="queue-kanban">
          <div className="flex gap-3 min-w-max pb-2">
            {KANBAN_SPALTEN.map((spalte) => {
              const zellen = gefiltert.filter((it) => (it.queueState ?? "") === spalte);
              return (
                <div key={spalte} className="w-56 shrink-0" data-testid={`kanban-${spalte}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[11px] font-semibold" title={spalte}>{statusLabel(spalte) ?? spalte}</span>
                    <span className="text-[11px] text-jarvis-muted">{zellen.length}</span>
                  </div>
                  <div className="space-y-2">
                    {zellen.slice(0, 12).map((it) => (
                      //_EMBER_CALM UX H: Karte blendet beim Eintritt in die Spalte
                      //_sanft ein (Spaltenwechsel = Remount → Animation einmal).
                      <div key={it.id} className="card p-2 simo-card-enter" title={warumLaeuftEsNicht(it) ?? it.title}>
                        <div className="text-xs font-medium leading-snug line-clamp-2">{it.title}</div>
                        <div className={`text-[10px] mt-1 ${prioFarbe(it.priority)}`} title={it.priority}>{prioLabel(it.priority)}</div>
                        {it.unmetDependencies.length > 0 && (
                          <div className={`text-[10px] mt-0.5 ${toneTextClass("waiting")}`}>⚑ {it.unmetDependencies.length} Deps</div>
                        )}
                        {!it.scopeVerified && it.writerScope && (
                          <div className={`text-[10px] ${toneTextClass("warning")}`}>⚠ Scope unverifiziert</div>
                        )}
                      </div>
                    ))}
                    {zellen.length > 12 && (
                      <div className="text-[10px] text-jarvis-muted">+{zellen.length - 12} weitere</div>
                    )}
                  </div>
                </div>
              );
            })}
            <div className="w-56 shrink-0">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[11px] font-semibold" title="(unverwaltet)">{statusLabel("(unverwaltet)") ?? "(unverwaltet)"}</span>
                <span className="text-[11px] text-jarvis-muted">{gefiltert.filter((it) => !it.queueState).length}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
